import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_AiBackground = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/ai-background',
			name: 'AI Background Generator — Generate Background | Lift',
			description:
				'Generate stunning AI backgrounds instantly. No watermark. Upload photo, describe your perfect backdrop, and transform product images with professional backgrounds in seconds. Try now!',
			url: 'https://liftapp.ai/ai-background',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/ai-background#product' },
				{ '@id': 'https://liftapp.ai/ai-background#howto' },
				{ '@id': 'https://liftapp.ai/ai-background#benefits' },
				{ '@id': 'https://liftapp.ai/ai-background#faq' },
				{ '@id': 'https://liftapp.ai/ai-background#why-lift' },
				{ '@id': 'https://liftapp.ai/ai-background#explore-tools' },
				{ '@id': 'https://liftapp.ai/ai-background#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/ai-background#product',
			name: 'AI Background Generator Online',
			description:
				'Transform photos with stunning AI-generated backgrounds in seconds. Create professional product backdrops, lifestyle scenes, and custom backgrounds, no watermarks.',
			url: 'https://liftapp.ai/ai-background',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/ai-background#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Let the AI do the work',
					text: 'Watch as Lift generates your perfect background automatically',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-background#benefits',
			name: 'Who can benefit from AI Background Generator?',
			description:
				"Lift's AI Background Generator is useful for professionals, creators, and businesses who want professional backgrounds for product photos, social media, marketing, or personal use.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Small business owners — Create professional product photos without studios or photographers. Generate clean, branded backgrounds instantly to boost trust and conversions in your store.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Content creators — Produce eye-catching visuals fast for Instagram, TikTok, and YouTube. Swap backgrounds to match trends and aesthetics while keeping your content visually consistent.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Marketers & SMM managers — Scale visual creation for ads, banners, and landing pages. Test multiple background styles from one image and iterate faster without extra production costs.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Freelancers — Deliver polished, client-ready visuals without advanced design skills. AI handles background generation in seconds, letting you focus on creativity and productivity.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Students & educators — Create professional visuals for presentations, projects, and social posts. No learning curve required — get immediate, impressive results for any academic or creative assignment.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'AI image background generator',
				description:
					'Example of how Lift AI Background Generator creates professional backgrounds for product photos and social media content.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/ai-background#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI Background Generator?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Background Generator is a premium online tool that instantly creates stunning, custom backgrounds for your photos with pixel-perfect precision — ideal for product photos, social content, or marketing materials.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Background Generator work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Simply upload your photo to Lift's AI background tool and describe what backdrop you want. Our AI automatically generates professional backgrounds in seconds — no photo editing experience needed. Download up to 20 images with your custom backgrounds.",
					},
				},
				{
					'@type': 'Question',
					name: 'Will it keep hair, edges, and fine details?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift AI Background Generator uses advanced edge detection to preserve delicate elements like hair, fur, shadows, and soft textures — so you get clean, natural-looking results without manual touch-ups.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-background#why-lift',
			name: "Why Lift's AI Background Generator stands out",
			description:
				"Lift's AI Background Generator combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Add AI background to photo',
				description:
					"Comparison of Lift's AI Background Generator benefits — professional quality, intelligent rendering, multi-platform access, and cost savings.",
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-background#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/ai-background#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI Background Generator',
					item: 'https://liftapp.ai/ai-background',
				},
			],
		},
	],
};
